<template>
  <section>
    <EmptyStateNotice
      v-if="showAlertMessage"
      :text="t('timeEnd')"
      variant="info"
      class="mb-3"
    />

    <div v-if="resultModal" class="result">
      <EmptyStateNotice
        :text="`${t('yourResult')}: ${result}%`"
        variant="info"
      />
      <div class="mt-3">
        <button class="btn-custom" type="button" @click="exitFromTest">
          {{ t('exitTest') }}
        </button>
      </div>
    </div>

    <div v-else class="test__body">
      <aside class="timer__body" v-if="!showAlertMessage">
        <div class="timer">
          <span>{{ t('time') }}:</span>
          <span>&nbsp;{{ getTimeTimer(timer) }}</span>
        </div>
        <div class="questions__all">
          <button
            v-for="(btn, index) in questions"
            :key="`q-num-${index}`"
            type="button"
            class="question__num"
            :class="selectedAnswerClass(btn.selectedAnswer)"
          >
            {{ index + 1 }}
          </button>
        </div>
        <div class="mt-3">
          <button
            type="button"
            class="btn-custom w-100"
            :disabled="disableEndBtn"
            @click="endTest(true)"
          >
            {{ t('exitTest') }}
          </button>
        </div>
      </aside>

      <div class="question__body" v-if="!showAlertMessage">
        <div class="border-dotted-bottom">
          <div class="question__header pb-2 mb-2">
            <h1 class="test__name bold">{{ test.name || t('tests') }}</h1>
            <p class="f-12 bold mb-0">
              {{ t('questionInTest') }}: {{ questions.length }}
            </p>
            <div class="hideOnBigSize">
              <p class="f-12 bold mb-0" v-if="Number(test.time || 0) > 0">
                {{ t('timerTime') }}: {{ getTimeTimer(timer) }}
              </p>
              <p v-else>{{ t('infinitytime') }}</p>
            </div>
          </div>
        </div>

        <div class="question___body p-2">
          <article
            v-for="(item, index) in questions"
            :key="`question-${item.qId}-${index}`"
            class="question-item mb-4"
          >
            <h2 class="question__title bold">
              {{ t('questionNum') }} №{{ item.num }}
            </h2>
            <p class="question__description" v-html="item.name"></p>

            <div v-if="item.type === 1 && Number(item.qCount || 0) <= 1">
              <label
                v-for="answer in item.answers"
                :key="`radio-${item.qId}-${answer.id}`"
                class="answer-row"
              >
                <input
                  type="radio"
                  :name="`question-${item.qId}`"
                  :checked="Number(item.selectedAnswer?.id || 0) === Number(answer.id || 0)"
                  @change="onSingleChoiceChange(item, answer)"
                />
                <span v-html="answer.name"></span>
              </label>
            </div>

            <div v-else-if="item.type === 2">
              <textarea
                v-model="item.selectedAnswer"
                class="answer-textarea"
                rows="5"
                :placeholder="t('writeYourAnswer')"
              />
              <button
                type="button"
                class="btn-custom w-100 mt-2"
                :disabled="disableTextAnswerBtn(item)"
                @click="onTextAnswerSubmit(item)"
              >
                {{ t('send') }}
              </button>
            </div>

            <div v-else>
              <label
                v-for="answer in item.answers"
                :key="`check-${item.qId}-${answer.id}`"
                class="answer-row"
              >
                <input
                  type="checkbox"
                  :checked="hasSelectedCheckbox(item, answer)"
                  @change="onMultiChoiceToggle(item, answer, $event)"
                />
                <span v-html="answer.name"></span>
              </label>
            </div>
          </article>
        </div>

        <div class="mt-3">
          <button
            type="button"
            class="btn-custom w-100"
            :disabled="disableEndBtn"
            @click="endTest(true)"
          >
            {{ t('exitTest') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import EmptyStateNotice from '@/components/common/EmptyStateNotice.vue';
import { finishSdotTestProcess, sendSdotTestAnswer } from '@/queries/task/useTaskQueries';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();

const test = ref({});
const questions = ref([]);
const sending = ref(false);
const showAlertMessage = ref(false);
const timer = ref(0);
const result = ref(0);
const resultModal = ref(false);

let timerInterval = null;

const lessonId = computed(() => Number(route.query.lessonId || 0));
const disableEndBtn = computed(() => !hasAnyAnswer());

watch(
  () => sending.value,
  (value) => loadingStore.set('test-page', value),
  { immediate: true }
);

onMounted(() => {
  test.value = parseJson(localStorage.getItem('task-test-current'), {});
  questions.value = normalizeQuestions(parseJson(localStorage.getItem('task-test-questions'), []));

  if (!test.value?.id || !questions.value.length) {
    goBackToTask();
    return;
  }

  if (Number(test.value?.time || 0) > 0) {
    timer.value = Number(test.value.time) * 60;
    startTimer();
  }

  topbarStore.setTitle(test.value?.name || t('tests'));
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => endTest(false),
  });
});

onBeforeUnmount(() => {
  stopTimer();
  loadingStore.hide('test-page');
});

function parseJson(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeQuestions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((question, index) => {
    const qCount = Number(question?.qCount || question?.correct_count || 1);
    const type = Number(question?.type || 1);
    let selectedAnswer = question?.selectedAnswer;
    if (selectedAnswer === undefined || selectedAnswer === null) {
      if (type === 2) selectedAnswer = '';
      else if (type === 3 || qCount > 1) selectedAnswer = [];
      else selectedAnswer = {};
    }

    return {
      ...question,
      num: Number(question?.num || index + 1),
      name: String(question?.name || question?.nameByLang || ''),
      answers: Array.isArray(question?.answers) ? question.answers : [],
      type,
      qCount,
      qId: Number(question?.qId || question?.id || 0),
      selectedAnswer,
      _savedAnswer: selectedAnswer,
    };
  });
}

function getTimeTimer(timeValue) {
  const total = Math.max(0, Number(timeValue || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = Math.floor(total % 60);
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function selectedAnswerClass(answer) {
  if (!answer) return '';
  if (Array.isArray(answer)) return answer.length ? 'selected' : '';
  if (typeof answer === 'object') return Number(answer?.id || 0) > 0 ? 'selected' : '';
  if (typeof answer === 'string') return answer.trim() ? 'selected' : '';
  return '';
}

function hasAnyAnswer() {
  return questions.value.some((item) => {
    if (Array.isArray(item.selectedAnswer)) return item.selectedAnswer.length > 0;
    if (typeof item.selectedAnswer === 'object' && item.selectedAnswer) {
      return Number(item.selectedAnswer.id || 0) > 0;
    }
    if (typeof item.selectedAnswer === 'string') return item.selectedAnswer.trim().length > 0;
    return false;
  });
}

function hasSelectedCheckbox(question, answer) {
  return Array.isArray(question.selectedAnswer)
    ? question.selectedAnswer.some((item) => Number(item?.id || 0) === Number(answer?.id || 0))
    : false;
}

async function onSingleChoiceChange(question, answer) {
  question.selectedAnswer = answer;
  await sendAnswer(question);
}

function onMultiChoiceToggle(question, answer, event) {
  const next = Array.isArray(question.selectedAnswer) ? [...question.selectedAnswer] : [];
  const index = next.findIndex((item) => Number(item?.id || 0) === Number(answer?.id || 0));

  if (event?.target?.checked && index === -1) next.push(answer);
  if (!event?.target?.checked && index > -1) next.splice(index, 1);
  question.selectedAnswer = next;
  sendAnswer(question);
}

function disableTextAnswerBtn(question) {
  const value = String(question?.selectedAnswer || '').trim();
  if (!value) return true;
  const prev = String(question?._savedAnswer || '').trim();
  return value === prev;
}

async function onTextAnswerSubmit(question) {
  await sendAnswer(question);
}

async function sendAnswer(question) {
  if (!question?.qId) return;
  const type = Number(question.type || 1);
  const qCount = Number(question.qCount || 1);

  let answers = [];
  if (type === 2) {
    const text = String(question.selectedAnswer || '').trim();
    if (!text) return;
    answers = [{ answer: text }];
  } else if (type === 3 || qCount > 1) {
    const selected = Array.isArray(question.selectedAnswer) ? question.selectedAnswer : [];
    if (!selected.length) return;
    answers = selected.map((item) => ({ id: Number(item?.id || 0) })).filter((item) => item.id > 0);
  } else {
    const id = Number(question.selectedAnswer?.id || 0);
    if (!id) return;
    answers = [{ id }];
  }

  sending.value = true;
  try {
    await sendSdotTestAnswer({
      lessonId: lessonId.value,
      questionId: question.qId,
      answers,
    });
    question._savedAnswer = Array.isArray(question.selectedAnswer)
      ? [...question.selectedAnswer]
      : question.selectedAnswer;
    localStorage.setItem('task-test-questions', JSON.stringify(questions.value));
  } catch {
    // legacy behavior: ignore detailed errors on answer submit
  } finally {
    sending.value = false;
  }
}

function startTimer() {
  stopTimer();
  timerInterval = setInterval(() => {
    if (timer.value <= 0) {
      stopTimer();
      showAlertMessage.value = true;
      endTest(false);
      return;
    }
    timer.value -= 1;
  }, 1000);
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

async function endTest(checkAnswers = true) {
  const hasSession = Boolean(localStorage.getItem('th')) && Boolean(localStorage.getItem('rh'));
  if (!hasSession) {
    goBackToTask();
    return;
  }
  if (checkAnswers && !hasAnyAnswer()) return;

  sending.value = true;
  try {
    const response = await finishSdotTestProcess({ lessonId: lessonId.value });
    result.value = Number(response?.result || 0);
    resultModal.value = true;
  } catch {
    clearSession();
    goBackToTask();
  } finally {
    sending.value = false;
  }
}

function exitFromTest() {
  clearSession();
  goBackToTask();
}

function clearSession() {
  localStorage.removeItem('th');
  localStorage.removeItem('rh');
  localStorage.removeItem('task-test-current');
  localStorage.removeItem('task-test-questions');
}

function goBackToTask() {
  const query = {};
  if (route.query.lessonId) query.lessonId = String(route.query.lessonId);
  if (route.query.lessonName) query.lessonName = String(route.query.lessonName);
  if (route.query.theme) query.theme = String(route.query.theme);
  if (route.query.day) query.day = String(route.query.day);
  if (route.query.section) query.section = String(route.query.section);

  router.push({ name: 'task', query });
}
</script>

<style scoped lang="scss">
.result {
  display: flex;
  padding: 16px;
  flex-direction: column;
  margin: 16px;
  max-width: 360px;
}

.questions__all {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
  gap: 4px;
}

.question__body {
  @media screen and (min-width: 768px) {
    width: 768px;
    margin-left: 32px;
  }
}

.timer {
  background: #4db7fe;
  color: #fff;
  height: 36px;
  border-radius: 2px;
  padding: 6px 48px;
  text-align: center;
  display: flex;
  justify-content: center;
}

.timer__body {
  @media screen and (max-width: 768px) {
    display: none;
  }
  @media screen and (min-width: 768px) {
    width: 288px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    height: 100%;
  }
}

.selected {
  background: #f2994a !important;
}

.question__num {
  padding: 6px 12px;
  width: 32px;
  display: flex;
  justify-content: center;
  border: none;
  color: #fff;
  border-radius: 2px;
  background: #36b453;
}

.test__body {
  @media screen and (max-width: 768px) {
    margin: 12px;
  }
  @media screen and (min-width: 768px) {
    margin: 36px 16px;
    display: flex;
  }
}

.btn-custom {
  border: none;
  border-radius: 2px;
  background: #36b453;
  color: #fff;
  text-transform: uppercase;
  padding: 8px 12px;
}

.question___body {
  overflow: auto;
  text-overflow: ellipsis;
}

.test__name {
  font-size: 18px;
  color: #4f4f4f;
}

.question-item {
  h2,
  p {
    color: #4f4f4f;
  }
}

.question__title {
  font-size: 14px;
}

.question__description {
  font-size: 15px;
}

.answer-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  color: #4f4f4f;
}

.answer-textarea {
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px;
  resize: vertical;
}
</style>
